/* eslint-disable max-depth */
import { companyRequest } from '@/utils/request'
import * as Sentry from '@sentry/react'

const videoTypeMap = ['mp4', 'avi', 'mov', 'mkv', 'wmv', 'flv', 'webm', 'rmvb']
const pdfTypeMap = ['pdf']

// 添加七牛域名
export const getFullQiNiuPath = src => {
    if (!src || typeof src !== 'string') {
        return ''
    }

    if (src.startsWith('http')) {
        return src
    }

    return PROJECT_CONFIG.qiniuHost + src
}

// 获取文件类型
export function getFileType(path) {
    return path && path.slice(path.lastIndexOf('.') + 1).toLowerCase()
}

// 获取文件封面 aliSize 为阿里可以显示的尺寸
const aliSize = [
    20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200, 210, 220, 230, 240, 250, 260,
    270, 290, 300, 310, 320, 350, 360, 400, 430, 440, 460, 480, 490, 500, 540, 560, 570, 580, 600, 640, 670, 720, 760,
    800, 960, 970, 1200, 1280
]

// 仅仅用于爆款专区jpg转webp
export const getFileCoverWebp = (path, config) => {
    if (
        /\.aliyuncs.com/.test(path) ||
        /\/\/video.sursung.com/.test(path) ||
        /\/\/images.sursung.com/.test(path) ||
        /\/ali2.a.kwimgs.com/.test(path) ||
        /\/\/assets.sursung.com/.test(path) ||
        /\/\/images-erp.sursung.com/.test(path)
    ) {
        const cleanPath = path.split('?x-oss-process')[0]
        const type = getFileType(cleanPath)
        if (!videoTypeMap.includes(type)) {
            return `${getFileCover(path, config)}/format,webp`
        } else {
            return getFileCover(path, config)
        }
    } else {
        return getFileCover(path, config)
    }
}

/**
 * 检测是否是阿里云
 */
export const checkIsAliCdn = path => {
    return (
        /\.aliyuncs.com/.test(path) ||
        /\/\/video.sursung.com/.test(path) ||
        /\/\/images.sursung.com/.test(path) ||
        /\/ali2.a.kwimgs.com/.test(path) ||
        /\/\/assets.sursung.com/.test(path) ||
        /\/\/images-erp.sursung.com/.test(path)
    )
}

/**
 * 检测是否是七牛
 */
export const checkIsQiniuCdn = path => {
    return (
        path.startsWith(PROJECT_CONFIG.qiniuHost) ||
        path.startsWith(PROJECT_CONFIG.qiniuHost2) ||
        path.startsWith(PROJECT_CONFIG.qiniuHost3)
    )
}

/**
 * 检测是否支持读取视频封面
 */
export const checkIsSupportReadVideoPoster = path => {
    return checkIsAliCdn(path) || checkIsQiniuCdn(path)
}

/**
 * 是否是假链接，展示需要调用后端接口获取实际视频地址
 * https://images.sursung.com/api/goods/externalLinkVideo/shopType/TouTiaoFXG/shopId/123/videold/456
 */
export const checkIsFakeLink = path => {
    if (!path) {
        return false
    }
    return path.includes('/videoLink/')
}

/**
 * 获取连接符号
 */
const getJoinSymbol = url => {
    return url?.includes('?') ? '&' : '?'
}
export const getFileCover = (path, config = { size: 300 }) => {
    if (!path || (typeof path === 'string' && path.trim() === '')) {
        return
    }

    // 阿里CDN处理
    if (/\.alicdn.com/.test(path)) {
        // 已经处理过的
        if (/_\d+x\d+/.test(path)) {
            return path
        }

        const { width = config.size, height = config.size } = config
        const newWidth = Math.round(width / 10) * 10 // 必须是10的倍数
        const newHeight = Math.round(height / 10) * 10
        const haveWidth = aliSize.find(size => size >= newWidth)
        const haveHeight = aliSize.find(size => size >= newHeight)

        if (haveWidth && haveHeight) {
            return `${path}_${haveWidth}x${haveHeight}q90.jpg`
        } else {
            return path
        }
    }

    // 阿里云
    if (checkIsAliCdn(path)) {
        // 某些场景允许用户输入图片地址，用户复制的地址会携带裁切参数
        const cleanPath = path.split('?x-oss-process')[0]
        const type = getFileType(cleanPath)
        const fullPath = cleanPath
        const { width = config.size, height = config.size } = config

        if (videoTypeMap.includes(type)) {
            return `${fullPath}${getJoinSymbol(
                fullPath
            )}x-oss-process=video/snapshot,t_2000,f_jpg,w_0,h_${height},m_fast`
        }

        return `${fullPath}${getJoinSymbol(fullPath)}x-oss-process=image/resize,m_fill,w_${width},h_${height}`
    }

    // 七牛处理
    if (checkIsQiniuCdn(path)) {
        const type = getFileType(path)
        const fullPath = getFullQiNiuPath(path)
        const { width = config.size, height = config.size } = config

        if (videoTypeMap.includes(type)) {
            return `${fullPath}${getJoinSymbol(fullPath)}vframe/png/offset/7/w/${width}/h/${height}`
        }

        if (pdfTypeMap.includes(type)) {
            return `${fullPath}${getJoinSymbol(fullPath)}odconv/jpg/page/1/density/150/quality/80/resize/${config.size}`
        }

        return `${fullPath}${getJoinSymbol(
            fullPath
        )}imageMogr2/gravity/center/auto-orient/thumbnail/!${width}x${height}r`
    }

    return path
}

// 获取七牛token
export const getQiNiuToken = async params => {
    const response = await companyRequest.get('/gyl/qiNiu/getToken', { params })

    if (response) {
        const { data, extra } = response

        return {
            token: data,
            keys: (extra && extra.picKeys) || []
        }
    }

    return null
}

// erp 处理图片 size不带参数会去掉压缩参数
// eslint-disable-next-line complexity
export const buildAliCdnImg = (srcImg, size) => {
    try {
        if (!srcImg) {
            return srcImg
        }

        if (srcImg.indexOf('ae01') !== -1) {
            return srcImg
        }

        if (
            srcImg.toLowerCase().indexOf('.alicdn.') !== -1 ||
            srcImg.toLowerCase().indexOf('.taobaocdn.') !== -1 ||
            srcImg.toLowerCase().indexOf('.tbsandbox.') !== -1
        ) {
            let jpgIndex = srcImg.toLowerCase().indexOf('.jpg_')
            let substring = 4
            if (jpgIndex === -1) {
                jpgIndex = srcImg.toLowerCase().indexOf('.png_')
                substring = 4
            }
            if (jpgIndex === -1) {
                jpgIndex = srcImg.toLowerCase().indexOf('.webp_')
                substring = 5
            }

            if (jpgIndex === -1) {
                jpgIndex = srcImg.toLowerCase().indexOf('.gif_')
                substring = 4
            }

            if (jpgIndex === -1) {
                jpgIndex = srcImg.toLowerCase().indexOf('.jpeg_')
                substring = 5
            }

            if (jpgIndex > 0) {
                if (size > 0) {
                    return `${srcImg.substring(0, jpgIndex + substring + 1) + size}x${size}.jpg`
                } else {
                    return srcImg.substring(0, jpgIndex + substring)
                }
            } else {
                if (srcImg.toLowerCase().endsWith('.310x310.jpg') || srcImg.toLowerCase().endsWith('.310x310.png')) {
                    return srcImg
                }

                let index = srcImg.toLowerCase().indexOf('_M2.SS2_')

                if (index > 0 && size > 0) {
                    return `${srcImg.substring(0, index + '_M2.SS2_'.length) + size}x${size}.jpg`
                } else if (index > 0) {
                    return srcImg
                }

                let pointIndex = srcImg.lastIndexOf('.')

                if (pointIndex > 0) {
                    let fileExt = srcImg.substring(pointIndex)

                    if (fileExt !== null && (fileExt.toLowerCase() === '.jpg' || fileExt.toLowerCase() === '.png')) {
                        let srcImg2 = srcImg.substring(0, pointIndex)
                        let sizeIndex = srcImg2.lastIndexOf('_')
                        let pointIndex2 = srcImg2.lastIndexOf('.')

                        if (pointIndex2 > sizeIndex) {
                            sizeIndex = pointIndex2
                        }

                        if (sizeIndex > 0) {
                            let sizeTxt = srcImg2.substring(sizeIndex + 1)
                            let sizes = sizeTxt.toLowerCase().split('x')
                            let _size = 0

                            if (sizes.length === 2) {
                                if (Number(sizes[0]) && Number(sizes[1])) {
                                    if (size) {
                                        return `${srcImg2.substring(0, sizeIndex + 1) + size}x${size}${fileExt}`
                                    } else {
                                        // 去掉参数
                                        return `${srcImg2.substring(0, sizeIndex)}${fileExt}`
                                    }
                                }
                            }
                        }
                    }
                }

                // 阿里巴巴国际站 60规格的不存在 兼容为50
                if (
                    srcImg.toLowerCase().indexOf('.alicdn.com/kf/') != -1 &&
                    (size === 60 || size === 90 || size === 400)
                ) {
                    switch (size) {
                        case 60:
                            size = 50
                            if (srcImg.startsWith('//')) {
                                srcImg = `http:${srcImg}`
                            }
                            break
                        case 90:
                        case 400:
                            return srcImg
                        default:
                            break
                    }
                }

                if (size) {
                    return `${srcImg}_${size}x${size}.jpg`
                }
            }
        }

        return srcImg
    } catch (error) {
        console.error('catchError', error)
        Sentry.setExtra('extra', {
            data: {
                srcImg: JSON.stringify(srcImg)
            },
            error
        })
        Sentry.captureException(new Error('erp 图片转化方法错误'))

        return srcImg
    }
}

/**
 * 改变富文本图片链接
 * @param {string} str
 * @returns
 */
export const changeRichTextImgSrc = str => {
    return (
        str &&
        str.replace(/<img [^>]*src=['"]([^'"]+)[^>]*>/g, (match, capture) => {
            return (
                match &&
                match.replace(/src=['"]([^'"]+)[^>]*?/g, src => {
                    let cap = capture.indexOf('images.sursung.com') > -1 || capture.indexOf('assets.sursung.com') > -1

                    if (cap && !/.gif$/.test(src)) {
                        const attachParam = str => {
                            return `${str}?x-oss-process=image/resize,m_mfit,w_2000/format,jpg`
                        }

                        // 已存在处理参数需要清除
                        const hasProcessParam = src.includes('?x-oss-process=')

                        if (hasProcessParam) {
                            const arr = src.split('?')
                            const oSrc = arr[0] || ''

                            return attachParam(oSrc)
                        }

                        return attachParam(src)
                    }

                    return src
                })
            )
        })
    )
}
